import { graphql } from 'react-relay';
import { JiraIssueFieldOptionAri } from '@atlassian/ari/jira/issue-field-option';
import type { selectIssueTransitionIssueAdjustmentsQuery as selectIssueTransitionIssueAdjustmentsQueryType } from '@atlassian/jira-relay/src/__generated__/selectIssueTransitionIssueAdjustmentsQuery.graphql';
import { FieldValueLookupError } from '../../../common/errors';
import type { PublicToInternalPropertyTransformerContext } from '../../../common/types';
import type { OptionsVisibility } from '../../../common/types/options-visibility';
import { applyOptionsVisibilityRule } from '../../../common/utils/apply-options-visibility-rule';
import { fetchFieldOptions } from '../../../common/utils/fetch-field-options';
import { getAvailableOptionsVisibilityRule } from '../../../common/utils/get-available-options-visibility-rule';
import { getResourceIdfromAri } from '../../../common/utils/get-resource-id-from-ari';
import { isNotEmptyString } from '../../../common/utils/is-not-empty-string';
import { singleValueLookup } from '../../../common/utils/single-value-lookup';

export type SelectInternalAllowedValue = {
	label: string;
	value: string;
};

export type SelectInternalValue =
	| null
	| {
			label: undefined;
			value: undefined;
	  }
	| {
			label: string;
			value: string;
	  };

export type SelectPublicGetValue = null | {
	id: string;
	value: string;
};

export type SelectPublicSetValue = null | string;

export const transformSelectSetOptionsVisibility = (
	optionsVisibility: OptionsVisibility,
	lookupValues: SelectInternalAllowedValue[],
): SelectInternalAllowedValue[] =>
	applyOptionsVisibilityRule(optionsVisibility, lookupValues, 'value');

export const transformSelectGetOptionsVisibility = (
	optionsVisibility: OptionsVisibility,
	lookupValues: SelectInternalAllowedValue[],
): OptionsVisibility => getAvailableOptionsVisibilityRule(optionsVisibility, lookupValues, 'value');

export const selectSetValueValidator = (value: unknown): value is SelectPublicSetValue =>
	value === null || isNotEmptyString(value);

export const selectSetValueTransformerOld = (
	value: SelectPublicSetValue,
	allowedValues?: SelectInternalAllowedValue[],
): SelectInternalValue => {
	if (value === null) {
		return null;
	}

	const foundValue = singleValueLookup<SelectInternalAllowedValue>(value, allowedValues, 'value');

	if (!foundValue) {
		throw new FieldValueLookupError();
	}

	return { value: foundValue.value, label: foundValue.label };
};

// eslint-disable-next-line jira/relay/must-use-inline
export const selectIssueTransitionIssueAdjustmentsQuery = graphql`
	query selectIssueTransitionIssueAdjustmentsQuery(
		$ari: ID!
		$filterById: JiraFieldOptionIdsFilterInput
	) {
		node(id: $ari) {
			... on JiraSingleSelectField {
				# eslint-disable-next-line relay/unused-fields
				id
				# eslint-disable-next-line relay/unused-fields
				fieldOptions(searchBy: "", filterById: $filterById) {
					edges {
						node {
							value: id
							label: value
						}
					}
				}
			}
		}
	}
`;

export const selectSetValueTransformer = async (
	inputValue: SelectPublicSetValue,
	_lookupValues: undefined,
	{
		tenantContext,
		viewContext,
		environment,
		fieldContext,
	}: PublicToInternalPropertyTransformerContext,
): Promise<SelectInternalValue | null> => {
	if (inputValue === null) return null;
	const optionAri = JiraIssueFieldOptionAri.create({
		siteId: tenantContext.cloudId,
		activationId: tenantContext.activationId,
		fieldId: fieldContext.fieldId,
		optionId: inputValue,
	}).toString();

	const response: NonNullable<
		NonNullable<selectIssueTransitionIssueAdjustmentsQueryType['response']['node']>['fieldOptions']
	> = await fetchFieldOptions<selectIssueTransitionIssueAdjustmentsQueryType>({
		tenantContext,
		viewContext,
		environment,
		fieldContext,
		optionARIs: [optionAri],
		nodePropertyName: 'fieldOptions',
		queryName: 'FETCH_SELECT',
		query: selectIssueTransitionIssueAdjustmentsQuery,
	});

	const option = response?.edges?.[0]?.node;

	if (option?.value !== optionAri) {
		throw new FieldValueLookupError(inputValue);
	}

	return {
		value: option.value,
		label: option.label ?? '',
	};
};

export const selectGetValueTransformerOld = (value: SelectInternalValue): SelectPublicGetValue => {
	if (!value || value.value === undefined) {
		return null;
	}

	const { label, value: id } = value;

	return { id, value: label };
};

export const selectGetValueTransformer = (
	internalValue: SelectInternalValue,
): SelectPublicGetValue => {
	if (!internalValue || internalValue.value === undefined) {
		return null;
	}

	return {
		id: getResourceIdfromAri(internalValue.value),
		value: internalValue.label,
	};
};
